"use client";

import { createContext, useEffect, useState } from "react";
import { createClient } from "../supabase/client";
import type { Session, UserAttributes } from "@supabase/supabase-js";
import { useRouter } from "next/navigation";
import { useSWRConfig } from "swr";
import { useSound } from "./sound-context";

type AuthContextType = {
  user: Session["user"] | null;
  loading: boolean;
  logout: () => Promise<void>;
  updateUser: (user: UserAttributes) => Promise<void>;
  refreshSession: () => Promise<void>;
};

export const AuthContext = createContext<AuthContextType>({
  user: null,
  loading: true,
  logout: () => Promise.resolve(),
  updateUser: () => Promise.resolve(),
  refreshSession: () => Promise.resolve(),
});

export function AuthProvider({ children }: { children: React.ReactNode }) {
  const router = useRouter();
  const [user, setUser] = useState<Session["user"] | null>(null);
  const [loading, setLoading] = useState(true);
  const supabase = createClient();

  const { engine } = useSound();

  const { mutate } = useSWRConfig();

  async function logout() {
    await supabase.auth.signOut();
    engine?.stopMusic();
    router.replace("/login");
    setUser(null);
    mutate(() => true, undefined, { revalidate: false });
  }

  async function refreshSession() {
    const {
      data: { user },
    } = await supabase.auth.getUser();

    const new_user = { ...user } as Session["user"];

    setUser(new_user);
  }

  async function updateUser(attributes: UserAttributes) {
    await supabase.auth.updateUser(attributes);
  }

  useEffect(() => {
    const getSession = async () => {
      const {
        data: { user },
      } = await supabase.auth.getUser();
      setUser(user ?? null);
      setLoading(false);

      supabase.auth.startAutoRefresh();
    };

    getSession();

    const {
      data: { subscription },
    } = supabase.auth.onAuthStateChange((event, session) => {
      if (event !== "INITIAL_SESSION") {
        setUser(session?.user ?? null);
        setLoading(false);
      }
    });

    return () => subscription?.unsubscribe();
  }, []);

  return (
    <AuthContext.Provider value={{ user, loading, logout, updateUser, refreshSession }}>
      {children}
    </AuthContext.Provider>
  );
}
